import { useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  IEditPrepItemPrepBatchStandardVariables,
  IPrepItemBatchType,
  editPrepItemPrepBatchStandard,
} from '../gql'
import { IPrepItemBatchStandardTableData } from '../hooks/usePrepItemBatchStandardsDataSource'

interface IEditPrepItemModalProps {
  visible: boolean
  onClose: () => void
  record: IPrepItemBatchStandardTableData | null
  batchTypes: IPrepItemBatchType[]
  loading?: boolean
  onSuccess?: () => void
}

const EditPrepItemModal = ({
  visible,
  onClose,
  record,
  batchTypes,
  onSuccess,
}: IEditPrepItemModalProps) => {
  const [prepBatch, setPrepBatch] = useState('')
  const [selectedBatchTypeId, setSelectedBatchTypeId] = useState<number | null>(
    null,
  )
  const [selectedUnit, setSelectedUnit] = useState('')
  const [standardUnitsPerBatch, setStandardUnitsPerBatch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // Filter batch types to only show ONHAND options
  const filteredBatchTypes = batchTypes.filter((bt) => bt.sourceType === 'PREP')

  const [editPrepItemPrepBatchStandardMutation] =
    useMutation<IEditPrepItemPrepBatchStandardVariables>(
      editPrepItemPrepBatchStandard,
    )

  useEffect(() => {
    if (record && visible && filteredBatchTypes.length > 0) {
      setPrepBatch(record.prepBatch)
      setStandardUnitsPerBatch(record.standardUnitsPerBatch.toString())

      // Find the batch type ID from the filtered batch types list
      const batchType = filteredBatchTypes.find(
        (bt) => bt.batchTypeName === record.prepBatch,
      )

      if (batchType) {
        setSelectedBatchTypeId(batchType.batchTypeId)
        setSelectedUnit(batchType.unit)
      } else {
        // Use the original unit from the record if batch type not found
        setSelectedUnit(record.standardUnits)
      }

      // Reset error and success states
      setError(null)
      setSuccess(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.prepItemId, visible]) // Only re-run when record ID or visibility changes, not on filteredBatchTypes changes

  const handleSave = async () => {
    if (!record) return

    // Check if the field is empty
    if (!standardUnitsPerBatch || standardUnitsPerBatch.trim() === '') {
      setError('Standard Units per Batch is required')
      return
    }

    const value = parseInt(standardUnitsPerBatch, 10)
    if (isNaN(value) || value <= 0) {
      setError('Please enter a valid positive integer greater than 0')
      return
    }

    if (!selectedBatchTypeId) {
      setError('Please select a valid prep batch type')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await editPrepItemPrepBatchStandardMutation({
        variables: {
          iPrepItemId: record.prepItemId,
          iBatchTypeId: selectedBatchTypeId,
          iStandardUnitsPerBatch: value,
        },
      })

      setSuccess(true)

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }

      // Close modal after showing success for a moment
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  if (!record) return null

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Prep Item Standard Unit</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Prep Item
            </Typography>
            <TextField
              value={record.prepItem}
              disabled
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Prep Batch</InputLabel>
              <Select
                value={prepBatch}
                onChange={(e) => {
                  const selectedName = e.target.value
                  setPrepBatch(selectedName)

                  // Find and set the batch type ID, unit
                  const batchType = filteredBatchTypes.find(
                    (bt) => bt.batchTypeName === selectedName,
                  )
                  if (batchType) {
                    setSelectedBatchTypeId(batchType.batchTypeId)
                    setSelectedUnit(batchType.unit)
                  }
                }}
                label="Prep Batch"
              >
                {filteredBatchTypes.map((type) => (
                  <MenuItem key={type.batchTypeId} value={type.batchTypeName}>
                    {type.batchTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Standard Units
            </Typography>
            <TextField
              value={selectedUnit || record.standardUnits}
              disabled
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Standard Units per Batch
            </Typography>
            <TextField
              value={standardUnitsPerBatch}
              onChange={(e) => {
                const value = e.target.value
                if (value === '' || /^\d+$/.test(value)) {
                  setStandardUnitsPerBatch(value)
                }
              }}
              placeholder="Enter integer value"
              fullWidth
              size="small"
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully updated
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}
          >
            <Button onClick={onClose} variant="outlined" disabled={isLoading}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={success || isLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EditPrepItemModal
